import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, AuditOutlined } from '@ant-design/icons';
import moment from 'moment';

import { DAYTIME_FORMAT } from '../constants';
import {
  getOrderAuditLog,
  operatorCreateOrderProduct,
  operatorDeleteOrderProduct,
  operatorUpdateOrderProduct,
} from '../modules/orders/api';
import { getAllPartnerItemTypes } from '../modules/partners/api';

/**
 * OVO-014 — operator-side editor for an order's products and
 * `productDetailsKnown` flag. Every mutation goes through endpoints
 * that write an AuditLog row identifying the operator + the diff.
 *
 * Use case: an order was created with wrong/missing product info (e.g.
 * a Compost order ended up with no products because productDetailsKnown
 * was left false). The operator can fix it from the planner without
 * dropping to SQL.
 *
 * Visible for ADMIN + USER (planner users). Partner viewers don't see
 * this component.
 */
export default function OrderProductsEditor({ order, onSaved }) {
  const [itemTypes, setItemTypes] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | <product id>
  const [submitting, setSubmitting] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [form] = Form.useForm();

  // Load partner item types once. Filter to this order's partner if known
  // so the operator picks from the right catalogue.
  useEffect(() => {
    let canceled = false;
    (async () => {
      const { response } = await getAllPartnerItemTypes();
      if (canceled || !Array.isArray(response)) return;
      const filtered = order?.partnerId ? response.filter((t) => t.partner?.id === order.partnerId) : response;
      setItemTypes(filtered);
    })();
    return () => {
      canceled = true;
    };
  }, [order?.partnerId]);

  const productById = useMemo(() => {
    const m = new Map();
    (order?.orderProducts || []).forEach((p) => m.set(p.id, p));
    return m;
  }, [order?.orderProducts]);

  const openAddModal = () => {
    form.resetFields();
    setEditing('new');
  };
  const openEditModal = (productId) => {
    const p = productById.get(productId);
    if (!p) return;
    // OVO-014 — pre-fill the modal with the EFFECTIVE weight (dispatcher
    // prefill ?? matching driver-captured CollectedItem). Otherwise an
    // operator opening Edit on a driver-weighed product would see an
    // empty field even though the row visibly shows "1.2 kg".
    const driverItem = (order?.collectedItems || []).find((ci) => ci.partnerItemTypeId === p.partnerItemTypeId);
    const effectiveWeight = p.weight != null ? p.weight : driverItem?.weight ?? null;
    form.setFieldsValue({
      partnerItemTypeId: p.partnerItemTypeId,
      quantity: p.quantity ?? 1,
      weight: effectiveWeight,
      notes: p.notes ?? '',
      // Stash the driver-captured value so we can show a hint in the modal.
      _driverCapturedWeight: driverItem?.weight ?? null,
      _driverCapturedBy: driverItem?.collectedByUser?.name || driverItem?.collectedByUser?.email || null,
    });
    setEditing(productId);
  };

  const closeModal = () => setEditing(null);

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    const payload = {
      partnerItemTypeId: values.partnerItemTypeId,
      quantity: values.quantity,
      // Treat blank weight as null so the driver/operator can fill it later.
      weight: values.weight === undefined || values.weight === '' ? null : Number(values.weight),
      notes: values.notes || '',
    };
    const promise =
      editing === 'new'
        ? operatorCreateOrderProduct(order.orderCode, payload)
        : operatorUpdateOrderProduct(order.orderCode, editing, payload);
    const { error } = await promise;
    setSubmitting(false);
    if (error) {
      message.error(error?.data?.message || 'Save failed');
      return;
    }
    message.success(editing === 'new' ? 'Product added.' : 'Product updated.');
    closeModal();
    onSaved?.();
  };

  const handleDelete = async (productId) => {
    const { error } = await operatorDeleteOrderProduct(order.orderCode, productId);
    if (error) {
      message.error(error?.data?.message || 'Delete failed');
      return;
    }
    message.success('Product removed.');
    onSaved?.();
  };

  const openAuditModal = async () => {
    setShowAudit(true);
    setAuditLoading(true);
    const { response, error } = await getOrderAuditLog(order.orderCode);
    setAuditLoading(false);
    if (error) {
      message.error('Could not load audit log');
      return;
    }
    setAuditEntries(Array.isArray(response) ? response : []);
  };

  const products = order?.orderProducts || [];

  return (
    <div className="OrderProductsEditor" style={{ marginTop: 8 }}>
      <Divider orientation="left" style={{ marginTop: 8 }}>
        <Space>
          <span>Products</span>
          <Tag color="orange" style={{ fontSize: 11 }}>
            operator editable
          </Tag>
        </Space>
      </Divider>

      {/* Top row: audit log + add product. The legacy `productDetailsKnown`
          toggle was removed — the flag is now derived automatically:
          adding the first product on a discovery-mode order flips it to
          true on the backend (and audit-logs the auto-flip). Operators
          shouldn't have to think about the flag separately. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Button icon={<AuditOutlined />} size="small" onClick={openAuditModal}>
          Audit log
        </Button>
        <Button type="primary" icon={<PlusOutlined />} size="small" onClick={openAddModal}>
          Add product
        </Button>
      </div>

      {products.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="No products on this order."
          description={
            order.productDetailsKnown === false
              ? 'The driver will be asked to add product items at pickup. If you already know what should be on the order, add them here — that will also stop the driver from being prompted.'
              : order.collectionPoint || (order.collectedItems || []).some((ci) => ci.collectionPointId != null)
                ? 'Collection-box flow: quantities are not declared upfront — the driver estimates each box fill on site (see Collected Items below).'
                : 'Add products if the order should have any. Otherwise leave empty.'
          }
        />
      ) : (
        <div className="opc-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {products.map((p) => {
            const ptype = p.partnerItemType || {};
            // OVO-019 — surface the same effective weight + provenance the
            // OrdersTable does. dispatcher (OrderProduct.weight) wins; falls
            // back to the matching PRODUCT CollectedItem captured by the
            // driver on site.
            const driverItem = (order?.collectedItems || []).find((ci) => ci.partnerItemTypeId === p.partnerItemTypeId);
            const dispatchWeight = p.weight;
            const driverWeight = driverItem?.weight ?? null;
            const effective = dispatchWeight != null ? dispatchWeight : driverWeight;
            const unit = ptype.unitOfMeasure || 'kg';
            const fromDriver = dispatchWeight == null && driverWeight != null;
            const pending = ptype.requiresWeight && dispatchWeight == null && driverWeight == null;
            const driverName = driverItem?.collectedByUser?.name || driverItem?.collectedByUser?.email;
            return (
              <div
                key={p.id}
                className="opc-row-editable"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 12px',
                  background: 'var(--surface-2)',
                  border: '1px solid #f0f0f0',
                  borderRadius: 4,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontWeight: 500 }}>{ptype.name || `Type #${p.partnerItemTypeId}`}</span>
                    {/* When dispatcher and driver values differ, dispatcher
                        wins on screen but a tooltip surfaces the driver's
                        original. Matches OrdersTable weight column. */}
                    {ptype.requiresWeight &&
                    dispatchWeight != null &&
                    driverWeight != null &&
                    driverWeight !== dispatchWeight ? (
                      <Tooltip
                        title={`Driver originally captured: ${driverWeight} ${unit}${driverName ? ` · ${driverName}` : ''}`}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            color: '#555',
                            borderBottom: '1px dashed #999',
                            cursor: 'help',
                          }}
                        >
                          {effective} {unit}
                        </span>
                      </Tooltip>
                    ) : (
                      <span style={{ fontSize: 13, color: '#555' }}>
                        {ptype.requiresWeight
                          ? effective != null
                            ? `${effective} ${unit}`
                            : `— ${unit}`
                          : `qty ${p.quantity} ${unit !== 'kg' ? unit : 'pcs'}`}
                      </span>
                    )}
                    {ptype.requiresWeight && p.quantity > 1 && (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>· qty {p.quantity}</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    {fromDriver && (
                      <Tooltip
                        title={
                          driverItem?.collectedAt
                            ? `${moment(driverItem.collectedAt).format(DAYTIME_FORMAT)}`
                            : 'Captured by driver'
                        }
                      >
                        <Tag color="blue">Weight by driver{driverName ? ` · ${driverName}` : ''}</Tag>
                      </Tooltip>
                    )}
                    {/* Dispatcher value AND a driver capture exists with a
                        different number → operator likely overrode. */}
                    {dispatchWeight != null && driverWeight != null && driverWeight !== dispatchWeight && (
                      <Tag color="purple">Operator-overridden</Tag>
                    )}
                    {/* Plain dispatcher prefill (no driver capture present). */}
                    {dispatchWeight != null && driverWeight == null && (
                      <Tag color="default">Pre-filled by dispatcher</Tag>
                    )}
                    {pending && <Tag color="orange">Awaiting driver weight</Tag>}
                    {ptype.requiresPhoto && <Tag color="default">requires photo</Tag>}
                    {ptype.requiresScan && <Tag color="default">requires scan</Tag>}
                  </div>
                  {p.notes ? (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{p.notes}</div>
                  ) : null}
                </div>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(p.id)}>
                  Edit
                </Button>
                <Popconfirm
                  title="Remove this product?"
                  description="The change will be audit-logged."
                  okText="Remove"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleDelete(p.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / edit modal */}
      <Modal
        title={editing === 'new' ? 'Add product to order' : 'Edit product'}
        open={editing !== null}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Save"
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="This change will be recorded in the order's audit log with your name."
        />
        <Form layout="vertical" form={form}>
          <Form.Item
            name="partnerItemTypeId"
            label="Product type"
            rules={[{ required: true, message: 'Pick a product type' }]}
          >
            <Select
              showSearch
              placeholder="Select product type"
              filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
              options={itemTypes.map((t) => ({
                value: t.id,
                label: `${t.name}${t.category ? ` · ${t.category}` : ''}${t.unitOfMeasure ? ` (${t.unitOfMeasure})` : ''}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Quantity"
            initialValue={1}
            rules={[{ required: true, message: 'Quantity required' }]}
          >
            <InputNumber min={1} style={{ width: 200 }} />
          </Form.Item>
          {/* OVO-014 — weight field. When the original came from a driver
              capture (no dispatcher prefill), expose that fact so the
              operator knows their edit will OVERRIDE the driver's value. */}
          <Form.Item shouldUpdate={(prev, cur) => prev._driverCapturedWeight !== cur._driverCapturedWeight} noStyle>
            {() => {
              const driverW = form.getFieldValue('_driverCapturedWeight');
              const driverBy = form.getFieldValue('_driverCapturedBy');
              const ptype = itemTypes.find((t) => t.id === form.getFieldValue('partnerItemTypeId')) || {};
              const unit = ptype.unitOfMeasure || 'kg';
              return (
                <Form.Item
                  name="weight"
                  label={
                    driverW != null
                      ? 'Weight (originally captured by driver — your change overrides it)'
                      : 'Weight (optional — leave empty if driver should capture)'
                  }
                  extra={
                    driverW != null ? (
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        Driver captured: {driverW} {unit}
                        {driverBy ? ` · ${driverBy}` : ''}. The driver's original entry stays in the audit trail.
                      </span>
                    ) : null
                  }
                >
                  <InputNumber
                    min={0}
                    decimalSeparator="."
                    style={{ width: 200 }}
                    parser={(v) =>
                      v == null
                        ? ''
                        : String(v)
                            .replace(/,/g, '.')
                            .replace(/[^\d.\-]/g, '')
                    }
                    formatter={(v) => (v == null || v === '' ? '' : String(v))}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="notes" label="Notes (optional)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Audit log modal */}
      <Modal
        title={`Audit log — ${order?.orderCode}`}
        open={showAudit}
        onCancel={() => setShowAudit(false)}
        footer={null}
        width={720}
      >
        {auditLoading ? (
          <Alert type="info" message="Loading…" />
        ) : auditEntries.length === 0 ? (
          <Alert type="info" message="No operator edits recorded for this order yet." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {auditEntries.map((entry) => (
              <div
                key={entry.id}
                style={{
                  padding: 10,
                  border: '1px solid #f0f0f0',
                  borderRadius: 4,
                  background: 'var(--surface-2)',
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <Tag color="purple">{entry.event}</Tag>
                  <strong>{entry.entityType}</strong>
                  {entry.entityId ? <span style={{ color: 'var(--text-muted)' }}>#{entry.entityId}</span> : null}
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {entry.user?.name || entry.user?.email || `user#${entry.userId}`} ·{' '}
                    {moment(entry.createdAt).format(DAYTIME_FORMAT)}
                  </span>
                </div>
                <pre
                  style={{
                    margin: 0,
                    fontSize: 11,
                    background: 'var(--surface-1)',
                    padding: 8,
                    borderRadius: 3,
                    border: '1px solid #f0f0f0',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(entry.changes, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
